import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Download } from '@mui/icons-material';

// Brand colors
const INDIGO_900 = '#312E81';
const INDIGO_700 = '#3730A3';
const INDIGO_600 = '#4F46E5';
const AMBER_500 = '#F59E0B';
const AMBER_100 = '#FEF3C7';
const AMBER_800 = '#92400E';
const GRAY_600 = '#4B5563';
const GRAY_400 = '#9CA3AF';
const WHITE = '#FFFFFF';

const THEME_NAMES_ID = {
  general: null,
  islamic: 'Islami',
  javanese: 'Jawa',
  arabic: 'Arab',
  christian: 'Kristen',
  sanskrit: 'Sanskerta',
  chinese: 'Tionghoa',
  modern: 'Modern',
  japanese: 'Jepang',
};

const THEME_NAMES_EN = {
  general: null,
  islamic: 'Islamic',
  javanese: 'Javanese',
  arabic: 'Arabic',
  christian: 'Christian',
  sanskrit: 'Sanskrit',
  chinese: 'Chinese',
  modern: 'Modern',
  japanese: 'Japanese',
};

// Wrap text to fit within maxWidth, returns array of lines
function getWrappedLines(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

// Draw a rounded rectangle path
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// Draw the amber decorative separator: ── ✦ ── ✦ ──
function drawSeparator(ctx, cx, y, totalWidth) {
  const lineLen = totalWidth * 0.18;
  const gap = 18;
  ctx.strokeStyle = AMBER_500;
  ctx.lineWidth = 2;

  // left line
  ctx.beginPath();
  ctx.moveTo(cx - gap * 2 - lineLen, y);
  ctx.lineTo(cx - gap * 2, y);
  ctx.stroke();

  // center line segments around stars
  ctx.beginPath();
  ctx.moveTo(cx - gap * 0.5, y);
  ctx.lineTo(cx + gap * 0.5, y);
  ctx.stroke();

  // right line
  ctx.beginPath();
  ctx.moveTo(cx + gap * 2, y);
  ctx.lineTo(cx + gap * 2 + lineLen, y);
  ctx.stroke();

  // stars
  ctx.fillStyle = AMBER_500;
  ctx.font = `${gap}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('✦', cx - gap * 1.4, y);
  ctx.fillText('✦', cx + gap * 1.4, y);
}

function drawCertificate(canvas, { name, meaning, theme, lang }) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width;
  const H = canvas.height;
  const cx = W / 2;

  // Scale factor relative to 1080-wide reference
  const scale = W / 1080;

  // --- Background gradient ---
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, '#EEF2FF');
  grad.addColorStop(0.5, '#FFFFFF');
  grad.addColorStop(1, '#FEF3C7');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // --- Indigo header bar ---
  const headerH = Math.round(200 * scale);
  ctx.fillStyle = INDIGO_700;
  ctx.fillRect(0, 0, W, headerH);

  // Header title
  ctx.fillStyle = WHITE;
  ctx.font = `bold ${Math.round(52 * scale)}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('CumaloApp', cx, headerH / 2);

  // Stars flanking header title
  const titleWidth = ctx.measureText('CumaloApp').width;
  ctx.fillStyle = AMBER_500;
  ctx.font = `${Math.round(38 * scale)}px 'Inter', sans-serif`;
  ctx.fillText('✦', cx - titleWidth / 2 - 40 * scale, headerH / 2);
  ctx.fillText('✦', cx + titleWidth / 2 + 40 * scale, headerH / 2);

  // Tagline in header (smaller, below title)
  const themeNames = lang === 'id' ? THEME_NAMES_ID : THEME_NAMES_EN;
  const themeName = themeNames[theme] || null;

  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = `${Math.round(22 * scale)}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const headerSubtitle = lang === 'id'
    ? 'Rekomendasi Nama Bayi'
    : 'Baby Name Recommendation';
  ctx.fillText(headerSubtitle, cx, headerH - 30 * scale);

  // --- Content area ---
  let y = headerH + 80 * scale;

  // Top amber separator
  drawSeparator(ctx, cx, y, W);
  y += 70 * scale;

  // Name (large, bold, indigo)
  ctx.fillStyle = INDIGO_900;
  ctx.font = `bold ${Math.round(64 * scale)}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const nameLines = getWrappedLines(ctx, name, W * 0.82);
  for (const line of nameLines) {
    ctx.fillText(line, cx, y);
    y += Math.round(76 * scale);
  }

  // Thin amber rule below name
  y += 16 * scale;
  ctx.strokeStyle = AMBER_500;
  ctx.lineWidth = 3;
  const ruleW = Math.min(W * 0.6, 600 * scale);
  ctx.beginPath();
  ctx.moveTo(cx - ruleW / 2, y);
  ctx.lineTo(cx + ruleW / 2, y);
  ctx.stroke();
  y += 36 * scale;

  // Meaning text (wrapped, gray)
  ctx.fillStyle = GRAY_600;
  ctx.font = `${Math.round(30 * scale)}px 'Inter', sans-serif`;
  ctx.textBaseline = 'top';
  const meaningLines = getWrappedLines(ctx, meaning, W * 0.78);
  for (const line of meaningLines) {
    ctx.fillText(line, cx, y);
    y += Math.round(42 * scale);
  }

  y += 32 * scale;

  // Theme badge (only if theme is not 'general')
  if (themeName) {
    const themeLabel = (lang === 'id' ? 'Tema: ' : 'Theme: ') + themeName;
    ctx.font = `${Math.round(24 * scale)}px 'Inter', sans-serif`;
    const badgeW = ctx.measureText(themeLabel).width + 48 * scale;
    const badgeH = 44 * scale;
    const bx = cx - badgeW / 2;

    roundedRect(ctx, bx, y, badgeW, badgeH, 22 * scale);
    ctx.fillStyle = AMBER_100;
    ctx.fill();
    ctx.strokeStyle = AMBER_500;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.fillStyle = AMBER_800;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(themeLabel, cx, y + badgeH / 2);

    y += badgeH + 32 * scale;
  }

  // Date
  const dateLocale = lang === 'id' ? 'id-ID' : 'en-US';
  const dateStr = new Date().toLocaleDateString(dateLocale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  ctx.fillStyle = GRAY_400;
  ctx.font = `${Math.round(22 * scale)}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(dateStr, cx, y);
  y += 50 * scale;

  // Bottom amber separator
  drawSeparator(ctx, cx, y, W);
  y += 60 * scale;

  // Tagline
  const tagline = lang === 'id'
    ? 'Nama pilihan untuk buah hati Anda'
    : 'The perfect name for your little one';
  ctx.fillStyle = GRAY_600;
  ctx.font = `italic ${Math.round(24 * scale)}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(tagline, cx, y);
  y += 40 * scale;

  // App URL
  ctx.fillStyle = INDIGO_600;
  ctx.font = `bold ${Math.round(24 * scale)}px 'Inter', sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('app.cumalochannel.com', cx, y);
}

const SIZES = {
  '916': { w: 1080, h: 1920 },
  '11': { w: 1080, h: 1080 },
};

function NameCertificate({ open, onClose, name, meaning, theme }) {
  const { i18n, t } = useTranslation();
  const canvasRef = useRef(null);
  const [format, setFormat] = useState('916');
  // `entered` becomes true only after the Dialog's enter transition completes,
  // ensuring the canvas is fully in the DOM before we attempt to draw.
  const [entered, setEntered] = useState(false);

  // Reset entered state whenever the dialog closes so it re-triggers on next open.
  useEffect(() => {
    if (!open) setEntered(false);
  }, [open]);

  // Draw only after the dialog has fully entered (canvas is guaranteed in DOM).
  useEffect(() => {
    if (!entered) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { w, h } = SIZES[format];
    canvas.width = w;
    canvas.height = h;

    document.fonts.ready.then(() => {
      drawCertificate(canvas, {
        name,
        meaning,
        theme,
        lang: i18n.language === 'id' ? 'id' : 'en',
      });
    });
  }, [entered, format, name, meaning, theme, i18n.language]);

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    const safeName = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    link.download = `sertifikat-${safeName}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // Preview width inside dialog
  const previewW = 220;
  const { w, h } = SIZES[format];
  const previewH = Math.round((previewW / w) * h);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEntered: () => setEntered(true) }}
    >
      <DialogTitle sx={{ fontWeight: 700, color: 'primary.dark' }}>
        {t('certificateDialogTitle')}
      </DialogTitle>
      <DialogContent>
        {/* Instruction callout */}
        <Box
          sx={{
            backgroundColor: '#FEF3C7',
            border: '1px solid #F59E0B',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#78350F', mb: 0.5 }}>
            📲 {t('certificateInstruction')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#92400E' }}>
            {t('certificateMotivation')}
          </Typography>
        </Box>

        {/* Format toggle + canvas preview side by side on wider dialog */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {/* Canvas preview */}
          <Box sx={{ flexShrink: 0 }}>
            <canvas
              ref={canvasRef}
              style={{
                width: previewW,
                height: previewH,
                borderRadius: 8,
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                display: 'block',
              }}
            />
          </Box>

          {/* Format toggle (vertical stack on the right) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {i18n.language === 'id' ? 'Ukuran' : 'Size'}
            </Typography>
            <ToggleButtonGroup
              value={format}
              exclusive
              onChange={(_, val) => { if (val !== null) setFormat(val); }}
              size="small"
              orientation="vertical"
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: '20px !important',
                  px: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  justifyContent: 'flex-start',
                  border: '1px solid rgba(0,0,0,0.12) !important',
                  mb: 0.5,
                },
              }}
            >
              <ToggleButton value="916">{t('certificateFormat916')}</ToggleButton>
              <ToggleButton value="11">{t('certificateFormat11')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="text" color="inherit">
          {i18n.language === 'id' ? 'Tutup' : 'Close'}
        </Button>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleDownload}
          sx={{ flex: 1 }}
        >
          {t('certificateDownload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NameCertificate;
